import axios from "axios"
import SnackbarComponent from "components/SnackBar"
import config from "config/config"
import { convertToRaw, EditorState } from "draft-js"
import draftToHtml from "draftjs-to-html"
import { convertToHTML } from "draft-convert"
import React, { useState } from "react"
import { Editor } from "react-draft-wysiwyg"
import { useSelector } from "react-redux"
import {
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Input,
  FormGroup,
  Label,
  Spinner,
  Modal,
  ModalFooter,
  ModalHeader,
  ModalBody,
} from "reactstrap"
import convertMarkdownToUnicode from "markdown-to-unicode"
import turndown from "turndown"
import "./index.css"

const CreatePost = () => {
  const turndownService = new turndown()
  const activeOrganization = useSelector(
    state => state.organizationReducer.activeOrganization,
  )
  const [postContent, setPostContent] = useState("")
  const mode = useSelector(state => state.Layout.layoutMode)
  const [postNow, setPostNow] = useState(true)
  const [editorState, setEditorState] = useState(EditorState.createEmpty())
  const [text, setText] = useState()
  const [images, setImages] = useState([])
  const [success, setSuccess] = useState(null)
  const [err, setErr] = useState(null)
  const [imagePreviews, setImagePreviews] = useState([])
  const [loading, setLoading] = useState(false)
  const [scheduleModal, setScheduleModal] = useState(false)
  const [scheduleDate, setScheduleDate] = useState("")
  const [postToInstagram, setPostToInstagram] = useState(false)

  const toggleScheduleModal = () => setScheduleModal(!scheduleModal)
  const userData = useSelector(state => state.Login.userData)

  const onEditorStateChange = editorState => {
    setEditorState(editorState)

    // Step 1: Convert editor state to raw HTML
    const rawHtml = draftToHtml(convertToRaw(editorState.getCurrentContent()))

    // Step 2: Convert HTML to Markdown
    const markdown = turndownService.turndown(rawHtml)

    // Step 3: Convert Markdown to Unicode
    const unicodeText = convertMarkdownToUnicode(markdown)
    console.log(unicodeText)

    setPostContent(unicodeText)
  }

  const schedulePost = async () => {
    setLoading(true)
    try {
      const scheduleTimestamp = Math.floor(
        new Date(scheduleDate).getTime() / 1000,
      )
      const minAllowedTime = Math.floor(Date.now() / 1000) + 600 // 10 minutes in the future

      if (scheduleTimestamp < minAllowedTime) {
        setErr("Scheduled time must be at least 10 minutes in the future.")
        setLoading(false)
        return
      }

      let attachedMedia = []
      let facebookMediaUrls = []

      if (images.length > 0) {
        const imageUploadPromises = images.map(async image => {
          const formData = new FormData()
          formData.append("source", image)
          formData.append(
            "access_token",
            activeOrganization.FacebookIntegrations[0].pageToken,
          )
          formData.append("published", false)

          const response = await axios.post(
            `https://graph.facebook.com/v20.0/${activeOrganization.FacebookIntegrations[0].pageId}/photos`,
            formData,
          )

          // Get the media_fbid (Facebook image ID)
          const mediaFbid = response.data.id

          // ✅ Fetch the public image URL (Use images field, not permalink_url)
          const mediaDetails = await axios.get(
            `https://graph.facebook.com/v20.0/${mediaFbid}?fields=images`,
            {
              headers: {
                Authorization: `Bearer ${activeOrganization.FacebookIntegrations[0].pageToken}`,
              },
            },
          )

          if (mediaDetails.data.images.length > 0) {
            const mediaUrl = mediaDetails.data.images[0].source // ✅ Get the first public image URL
            facebookMediaUrls.push(mediaUrl)
          }

          return { media_fbid: mediaFbid }
        })

        attachedMedia = await Promise.all(imageUploadPromises)
      }

      // Schedule post to Facebook
      await axios.post(
        `https://graph.facebook.com/v20.0/${activeOrganization.FacebookIntegrations[0].pageId}/feed`,
        {
          message: postContent,
          scheduled_publish_time: scheduleTimestamp,
          published: false,
          attached_media: attachedMedia.length > 0 ? attachedMedia : undefined,
          access_token: activeOrganization.FacebookIntegrations[0].pageToken,
        },
      )

      if (postToInstagram) {
        try {
          const formData = {
            message: postContent,
            scheduleTimestamp,
            mediaUrls: facebookMediaUrls,
          }

          await axios.post(
            `${config.backendUrl}/instagram/schedule-instagram-post/${activeOrganization.id}/${activeOrganization.InstagramIntegrations[0].id}`,
            formData,
            {
              headers: {
                Authorization: `Bearer ${userData.token}`,
              },
            },
          )

          setSuccess("Instagram post scheduled successfully on backend.")
        } catch (error) {
          console.error("Error scheduling Instagram post:", error)
          setErr("Failed to schedule Instagram post.")
        }
      }

      setSuccess("Post scheduled successfully.")
      setScheduleModal(false)
    } catch (err) {
      console.error("Error scheduling post:", err)
      setErr("Error while scheduling post.")
    } finally {
      setLoading(false)
    }
  }

  const handlePostCreation = async () => {
    setLoading(true)

    try {
      let mediaFbIds = []
      let facebookMediaUrls = []
      let attachedMedia = []

      // ✅ Step 1: Upload Images to Facebook First
      const uploadImageToFacebook = async file => {
        const formData = new FormData()
        formData.append("source", file)
        formData.append("published", false)
        formData.append(
          "access_token",
          activeOrganization.FacebookIntegrations[0].pageToken,
        )

        const response = await axios.post(
          `https://graph.facebook.com/v20.0/${activeOrganization.FacebookIntegrations[0].pageId}/photos`,
          formData,
        )

        return response.data.id // Returns Facebook media_fbid
      }

      if (images.length > 0) {
        mediaFbIds = await Promise.all(images.map(uploadImageToFacebook))

        // ✅ Step 2: Retrieve Public Image URLs from Facebook
        for (const mediaFbid of mediaFbIds) {
          const mediaDetails = await axios.get(
            `https://graph.facebook.com/v20.0/${mediaFbid}?fields=images`,
            {
              headers: {
                Authorization: `Bearer ${activeOrganization.FacebookIntegrations[0].pageToken}`,
              },
            },
          )

          if (mediaDetails.data.images.length > 0) {
            const mediaUrl = mediaDetails.data.images[0].source
            facebookMediaUrls.push(mediaUrl)
          }
        }

        // ✅ Step 3: Prepare attached_media for Facebook post
        attachedMedia = mediaFbIds.map(id => ({ media_fbid: id }))
      }

      // ✅ Step 4: Post to Facebook First
      await axios.post(
        `https://graph.facebook.com/v20.0/${activeOrganization.FacebookIntegrations[0].pageId}/feed`,
        {
          message: postContent,
          attached_media: attachedMedia.length > 0 ? attachedMedia : undefined,
          access_token: activeOrganization.FacebookIntegrations[0].pageToken,
        },
      )

      setSuccess("🎉 Post published successfully on Facebook!")

      // ✅ Step 5: Post to Instagram (if selected)
      if (postToInstagram) {
        if (
          !activeOrganization?.InstagramIntegrations[0]?.pageId ||
          !activeOrganization?.InstagramIntegrations[0]?.pageToken
        ) {
          setErr(
            "Instagram integration is missing. Please check your settings.",
          )
          setLoading(false)
          return
        }

        let mediaContainerIds = []

        // ✅ Step 6: Upload Images to Instagram Using Facebook Image URLs
        for (const imageUrl of facebookMediaUrls) {
          const uploadResponse = await axios.post(
            `https://graph.facebook.com/v20.0/${activeOrganization.FacebookIntegrations[0].pageId}/media`,
            {
              image_url: imageUrl,
              caption: postContent,
              access_token:
                activeOrganization.FacebookIntegrations[0].pageToken,
            },
          )

          if (!uploadResponse.data.id) {
            throw new Error("Failed to upload media to Instagram.")
          }

          mediaContainerIds.push(uploadResponse.data.id)
        }

        let creationId

        // ✅ Step 7: Handle Carousel vs Single Image Post
        if (mediaContainerIds.length > 1) {
          const carouselResponse = await axios.post(
            `https://graph.facebook.com/v20.0/${activeOrganization.InstagramIntegrations[0].pageId}/media`,
            {
              media_type: "CAROUSEL",
              children: mediaContainerIds,
              caption: postContent,
              access_token:
                activeOrganization.InstagramIntegrations[0].pageToken,
            },
          )

          if (!carouselResponse.data.id) {
            throw new Error("Failed to create carousel post.")
          }

          creationId = carouselResponse.data.id
        } else {
          creationId = mediaContainerIds[0] // Single post
        }

        // ✅ Step 8: Publish the Instagram Post
        await axios.post(
          `https://graph.facebook.com/v20.0/${activeOrganization.InstagramIntegrations[0].pageId}/media_publish`,
          {
            creation_id: creationId,
            access_token: activeOrganization.InstagramIntegrations[0].pageToken,
          },
        )

        setSuccess("🎉 Post published successfully on Instagram!")
      }

      // ✅ Reset UI states
      setPostContent("")
      setEditorState(EditorState.createEmpty())
      setImages([])
      setImagePreviews([])
    } catch (error) {
      console.error("Error creating post:", error)
      setErr("Error while creating post. Please try again.")
    } finally {
      setLoading(false)
    }
  }

  const handleImageChange = e => {
    const files = Array.from(e.target.files)
    setImages(prevImages => [...prevImages, ...files])

    const newImagePreviews = files.map(file => URL.createObjectURL(file))
    setImagePreviews(prevPreviews => [...prevPreviews, ...newImagePreviews])
  }

  const removeImage = index => {
    const updatedImages = images.filter((_, i) => i !== index)
    const updatedPreviews = imagePreviews.filter((_, i) => i !== index)

    setImages(updatedImages)
    setImagePreviews(updatedPreviews)
  }

  const toolbarOptions = {
    options: ["inline", "list", "emoji"],
    inline: {
      options: ["bold", "italic"],
    },
    list: {
      options: ["unordered", "ordered"],
    },

    emoji: {},
  }
  return (
    <>
      <SnackbarComponent
        err={err}
        success={success}
        setErr={setErr}
        setSuccess={setSuccess}
      />
      <Card className="br" style={{ minHeight: "350px" }}>
        {loading ? (
          <div
            style={{
              minHeight: "350px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Spinner size="lg" color="primary" />
          </div>
        ) : (
          <div>
            <CardBody>
              <Row className="mb-2">
                <Col>
                  <h4
                    style={{
                      color: mode === "dark" ? "white" : "#2990DF",
                    }}
                    className="card-title text-uppercase"
                  >
                    Create Facebook Post
                  </h4>
                </Col>
              </Row>

              <Row className="px-2">
                <Col style={{}}>
                  <FormGroup>
                    <Editor
                      editorState={editorState}
                      toolbarClassName="editor-toolbar"
                      wrapperClassName="editor-wrapper"
                      editorClassName="editor-editor"
                      onEditorStateChange={onEditorStateChange}
                      toolbar={toolbarOptions}
                    />
                  </FormGroup>
                </Col>
              </Row>

              <Row className="px-2 mt-1">
                <Col>
                  <div className="d-flex flex-wrap">
                    {imagePreviews.map((preview, index) => (
                      <div
                        key={index}
                        style={{ position: "relative", margin: "5px" }}
                      >
                        <img
                          src={preview}
                          alt={`preview-${index}`}
                          style={{
                            width: "50px",
                            height: "50px",
                            borderRadius: "10px",
                          }}
                        />
                        <Button
                          color="danger"
                          size="sm"
                          style={{
                            position: "absolute",
                            top: 0,
                            right: 0,
                            borderRadius: "50%",
                          }}
                          onClick={() => removeImage(index)}
                        >
                          <span className="mdi mdi-trash-can"></span>
                        </Button>
                      </div>
                    ))}
                  </div>
                  <FormGroup>
                    <Label for="imageUpload">Upload Images</Label>
                    <Input
                      type="file"
                      id="imageUpload"
                      accept="image/*"
                      multiple
                      onChange={handleImageChange}
                    />
                  </FormGroup>
                </Col>
              </Row>

              {userData.role !== "viewer" && (
                <Row className="mt-3">
                  <Col className="text-right">
                    <Button
                      style={{
                        backgroundColor:
                          mode === "dark" ? "#2990DF" : "#2990DF",
                        border: "none",
                      }}
                      size="md"
                      onClick={toggleScheduleModal}
                      disabled={postContent === "" && images.length === 0}
                    >
                      Post to Facebook
                    </Button>
                  </Col>
                </Row>
              )}
            </CardBody>
          </div>
        )}
      </Card>
      <Modal isOpen={scheduleModal} toggle={toggleScheduleModal} centered>
        <ModalHeader toggle={toggleScheduleModal}>
          Schedule or Post Now
        </ModalHeader>
        <ModalBody>
          {/* Post Timing Selection */}
          <FormGroup className="d-flex justify-content-between">
            <Button
              style={{
                backgroundColor: postNow
                  ? mode === "dark"
                    ? "#2990DF"
                    : "#2990DF"
                  : mode === "dark"
                    ? "#333"
                    : "#f0f0f0",
                border: "#2990DF",
                color: postNow ? "#fff" : mode === "dark" ? "#fff" : "#000",
              }}
              onClick={() => {
                setPostNow(true)
                setScheduleDate("")
              }}
            >
              📢 Instant Post
            </Button>
            <Button
              style={{
                backgroundColor: !postNow
                  ? mode === "dark"
                    ? "#2990DF"
                    : "#2990DF"
                  : mode === "dark"
                    ? "#333"
                    : "#f0f0f0",
                border: "#2990DF",
                color: !postNow ? "#fff" : mode === "dark" ? "#fff" : "#000",
              }}
              onClick={() => setPostNow(false)}
            >
              ⏳ Schedule Post
            </Button>
          </FormGroup>

          {/* Date Input (Only for Scheduled Posts) */}
          {!postNow && (
            <FormGroup>
              <Label for="scheduleDate">Select Schedule Date</Label>
              <Input
                type="datetime-local"
                id="scheduleDate"
                value={scheduleDate}
                onChange={e => setScheduleDate(e.target.value)}
                placeholder="Select Date & Time"
              />
            </FormGroup>
          )}

          {/* Custom Styled Checkbox for "Post to Facebook" */}
          {activeOrganization.InstagramIntegrations[0] && (
            <FormGroup className="mt-3">
              <Label check className="d-flex align-items-center">
                <div
                  className={`custom-checkbox ${postToInstagram ? "checked" : ""}`}
                  onClick={() => setPostToInstagram(!postToInstagram)}
                >
                  {postToInstagram && <span className="checkmark">✔</span>}
                </div>
                <span className="ml-2">Post to Instagram</span>
              </Label>
            </FormGroup>
          )}
        </ModalBody>

        <ModalFooter>
          <Button
            style={{
              backgroundColor: mode === "dark" ? "#2990DF" : "#2990DF",
              border: "none",
            }}
            onClick={postNow ? handlePostCreation : schedulePost}
            disabled={!postNow && !scheduleDate}
          >
            {postNow ? "Post Now" : "Schedule"}
          </Button>
          <Button onClick={toggleScheduleModal}>Cancel</Button>
        </ModalFooter>
      </Modal>
    </>
  )
}

export default CreatePost
